import useNotifications from '@context/notifications/useNotificationContext';
import { AxiosError } from 'axios';
import { useCallback } from 'react';

/**
 * Reschedule conflicts (409 `reschedule_conflicts`) are surfaced through a
 * dedicated resolution modal by the calling feature, so the generic error
 * toast must be suppressed for them.
 */
const isRescheduleConflictError = (error: unknown): boolean => {
  const axiosError = error as AxiosError<{ code?: string }>;
  return axiosError?.response?.status === 409 && axiosError?.response?.data?.code === 'reschedule_conflicts';
};

export enum ErrorType {
  API_ERROR = 'API_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export class AppError extends Error {
  public readonly type: ErrorType;
  public readonly originalError?: unknown;

  constructor(message: string, type: ErrorType, originalError?: unknown) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.originalError = originalError;
  }
}

/**
 * Hook for comprehensive error handling with smart classification
 */
const useSmartErrorHandling = () => {
  const { openNotification } = useNotifications();

  /**
   * Classify error type based on error characteristics
   */
  const classifyError = useCallback((error: Error | AxiosError | AppError): ErrorType => {
    // Check if it's already classified
    if (error instanceof AppError) {
      return error.type;
    }

    if ('response' in error) {
      return ErrorType.API_ERROR;
    }

    if ('request' in error) {
      return ErrorType.NETWORK_ERROR;
    }

    if (error instanceof Error) {
      return ErrorType.VALIDATION_ERROR;
    }

    return ErrorType.UNKNOWN_ERROR;
  }, []);

  /**
   * Extract appropriate error message based on error type
   */
  const extractErrorMessage = useCallback((error: Error | AxiosError | AppError, errorType: ErrorType): string => {
    if (error instanceof AppError) {
      return error.message;
    }

    switch (errorType) {
      case ErrorType.API_ERROR:
        if ('response' in error) {
          const axiosError = error as AxiosError;
          const errorData = axiosError?.response?.data as { detail?: string };

          // Extract detail if it's a string, otherwise use default
          if (
            errorData &&
            typeof errorData === 'object' &&
            'detail' in errorData &&
            typeof errorData.detail === 'string'
          ) {
            return errorData.detail;
          }

          if (axiosError?.response?.statusText) {
            return `${axiosError.response.status}: ${axiosError.response.statusText}`;
          }
        }
        return 'API request failed';

      case ErrorType.VALIDATION_ERROR:
        if (error instanceof Error) {
          return error.message;
        }
        return 'Validation failed';

      case ErrorType.NETWORK_ERROR:
        return 'Network connection failed';

      case ErrorType.UNKNOWN_ERROR:
      default:
        return 'An unexpected error occurred';
    }
  }, []);

  /**
   * Handle errors intelligently - only show notifications for non-API errors
   * API errors are handled by the global interceptor
   */
  const handleError = useCallback(
    (
      error: Error | AxiosError | AppError,
      options?: {
        customMessage?: string;
        customTitle?: string;
        customType?: 'Light' | 'Danger' | 'Success' | 'Warning' | 'Info';
        forceShow?: boolean; // Force show notification even for API errors
      }
    ) => {
      // Reschedule conflicts are handled by a dedicated modal; never toast them.
      if (isRescheduleConflictError(error)) {
        throw error;
      }

      const errorType = classifyError(error);

      // Let API errors be handled by the global interceptor unless forceShow is true
      // Only show notifications for validation and other client-side errors
      if (errorType !== ErrorType.API_ERROR || options?.forceShow) {
        const message = options?.customMessage || extractErrorMessage(error, errorType);
        const title = options?.customTitle || getErrorTitle(errorType);
        const type = options?.customType || 'Warning';

        openNotification('topRight', {
          title: String(title),
          description: String(message),
          type,
        });
      }

      // Re-throw so calling code can still handle it if needed
      throw error;
    },
    [classifyError, extractErrorMessage, openNotification]
  );

  /**
   * Wrapper for API calls with smart error handling
   */
  const callWithSmartErrorHandling = useCallback(
    async <T>(apiCall: () => Promise<T>): Promise<T> => {
      try {
        return await apiCall();
      } catch (error) {
        handleError(error as Error | AxiosError | AppError);
        throw error;
      }
    },
    [handleError]
  );

  /**
   * Create validation errors
   */
  const createValidationError = useCallback((message: string) => {
    return new AppError(message, ErrorType.VALIDATION_ERROR);
  }, []);

  return {
    handleError,
    callWithSmartErrorHandling,
    createValidationError,
    classifyError,
    extractErrorMessage,
    ErrorType,
    AppError,
  };
};

const getErrorTitle = (errorType: ErrorType): string => {
  switch (errorType) {
    case ErrorType.VALIDATION_ERROR:
      return 'Validation Error';
    case ErrorType.NETWORK_ERROR:
      return 'Connection Error';
    case ErrorType.API_ERROR:
      return 'Error';
    case ErrorType.UNKNOWN_ERROR:
    default:
      return 'Error';
  }
};

export default useSmartErrorHandling;
